import Card from '@common/components/card';
import { Container } from '@common/components/container';
import FiltersToolbar from '@common/components/filters-toolbar/filters-toolbar';
import { selected } from '@modules/organisation/selectors/organisation';
import * as organisationSelectors from '../../../organisation/selectors/organisation';
import { EPredicateFilters } from '@common/components/predicates-filter';
import { EPredicateFields, EPredicateOperators } from '@common/definitions';
import { useAppSelector, useDidComponentMount } from '@common/hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Overview from '@common/components/overview';
import Api, { useRequest } from '@common/services/api';
import { Accordion } from '@szhsin/react-accordion';
import AccordionItem from '@modules/performance/components/accordion-item/accordion-item';
import {
  PerformanceRatingResponse,
  RatingScaleData,
} from '@modules/performance/types';
import Icon from '@common/components/icon';
import './styles.scss';
import Spinner from '@common/components/spinner';
import Placeholder from '@common/components/placeholder';
import Skeleton from '@modules/performance/components/skeleton/skeleton';
import { Button } from '@common/components/button';
import { exportPerformanceRatingReportPDF } from '@modules/performance/utils/export-performance-rating-report-pdf';
import ExportModal from '@modules/performance/components/export-modal/export-modal';
import PerformanceTooltip from '@modules/performance/components/performance_tooltip/performance_tooltip';

const PerformanceRatingContainer = () => {
  const { t } = useTranslation();
  const organisation = useAppSelector(selected);
  const networks = useAppSelector(organisationSelectors.networks);
  const functions = useAppSelector(organisationSelectors.functions);
  const didMount = useDidComponentMount();

  const { data: performanceData, loading: performanceLoading } = useRequest(
    `/v1/organisations/${organisation.id}/performance/reports/summary`
  ) as unknown as { data: PerformanceRatingResponse; loading: boolean };

  const categoryFilterOptions = [
    { label: t('performance:category'), value: 'category' },
    {
      label: t('performance:function'),
      value: 'function',
    },
  ];

  const [predicates, setPredicates] = useState([
    {
      filter: EPredicateFilters[EPredicateFields.COMPETENCY_GROUP_BY],
      value: {
        attribute: EPredicateFields.COMPETENCY_GROUP_BY,
        comparison: 'eq' as const,
        value: 'category',
      },
      options: categoryFilterOptions,
    },
    {
      filter: EPredicateFilters[EPredicateFields.NETWORK],
      value: {
        attribute: EPredicateFields.NETWORK,
        comparison: EPredicateOperators.IN,
        value: [],
      },
      options: networks || [],
    },
    {
      filter: EPredicateFilters[EPredicateFields.FUNCTION],
      value: {
        attribute: EPredicateFields.FUNCTION,
        comparison: EPredicateOperators.IN,
        value: [],
      },
      options: functions || [],
    },
  ]);

  const total = performanceData?.total_evaluations;

  const getPercentage = (value: number) => {
    if (!total || total === 0) return 0;
    return (value / total) * 100;
  };

  const legendItems = performanceData?.rating_scales?.map(
    (scale: RatingScaleData) => ({
      id: scale.id,
      label: scale.label,
      color: scale.color,
      total_evaluations: scale.total_evaluations,
    })
  );

  const distributionItems = legendItems?.filter(
    (item: any) => item.total_evaluations > 0
  );

  const [openItemIndex, setOpenItemIndex] = useState<number | null>(null);
  const [competenciesData, setCompetenciesData] = useState<Record<string, any>>(
    {}
  );
  const [loadingCompetencies, setLoadingCompetencies] = useState<
    Record<string, boolean>
  >({});
  const [showExportModal, setShowExportModal] = useState(false);

  const handleItemClick = (index: number) => {
    const newIndex = openItemIndex === index ? null : index;
    setOpenItemIndex(newIndex);
  };

  const [group_by, networkIds, functionIds] = useMemo(() => {
    return [
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.COMPETENCY_GROUP_BY]
      )?.value.value as string,
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.NETWORK]
      )?.value.value,
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.FUNCTION]
      )?.value.value,
    ];
  }, [predicates]);

  const payload = useMemo(
    () => ({
      ...(group_by && group_by !== '' ? { groupBy: group_by } : {}),
      ...(networkIds ? { network_ids: networkIds } : {}),
      ...(functionIds ? { function_ids: functionIds } : {}),
      // version: 1,
    }),
    [group_by, networkIds, functionIds]
  );

  const {
    data: distributionData,
    reset,
    loading: distributionLoading,
  } = useRequest(
    `/v1/organisations/${organisation.id}/performance/reports/distribution`,
    {
      payload,
    }
  ) as unknown as {
    data: { categories?: any[]; functions?: any[] };
    reset: () => void;
    loading: boolean;
  };

  const displayData =
    group_by === 'category'
      ? distributionData?.categories
      : distributionData?.functions;

  useEffect(() => {
    if (!didMount) return;
    reset();
  }, [payload, didMount, reset]);

  useEffect(() => {
    const fetchAllCompetencies = async () => {
      if (!displayData || displayData.length === 0) return;

      const promises = displayData.map(async (item: any) => {
        if (competenciesData[item.id]) return;

        const queryParam =
          group_by === 'function'
            ? `function_ids[]=${item.id}`
            : `category_ids[]=${item.id}`;

        try {
          setLoadingCompetencies((prev) => ({ ...prev, [item.id]: true }));
          const response = await Api.get(
            `/v1/organisations/${organisation.id}/performance/reports/competencies?${queryParam}`
          );
          return { id: item.id, data: response.data };
        } catch (error) {
          console.error('Error fetching competencies:', error);
          return null;
        } finally {
          setLoadingCompetencies((prev) => ({ ...prev, [item.id]: false }));
        }
      });

      const results = await Promise.all(promises);
      const newCompetenciesData: Record<string, any> = {};
      results.forEach((result) => {
        if (result) {
          newCompetenciesData[result.id] = result.data;
        }
      });

      setCompetenciesData((prev) => ({ ...prev, ...newCompetenciesData }));
    };

    fetchAllCompetencies();
  }, [displayData, group_by, organisation.id]);

  const handleExportPDF = async () => {
    const allCompetencies: any[] = [];

    if (displayData && displayData.length > 0) {
      displayData.forEach((item: any) => {
        const cachedData = competenciesData[item.id];
        if (cachedData?.competencies) {
          cachedData.competencies.forEach((competency: any) => {
            allCompetencies.push({
              ...competency,
              category: item.name,
            });
          });
        }
      });
    }

    await exportPerformanceRatingReportPDF({
      organisationName: organisation?.name || '',
      ratingScales: performanceData?.rating_scales || [],
      totalEvaluations: performanceData?.total_evaluations || 0,
      distributionData: allCompetencies,
      organisationLogo: organisation?.brand_icon || '',
      groupBy: group_by,
    });
  };

  const handleExportCSV = () => {
    // TODO: Implement CSV export
    console.log('CSV export');
  };

  const handleExportXLS = () => {
    // TODO: Implement XLS export
    console.log('XLS export');
  };

  return (
    <Container name="PerformanceRatings">
      <Container.Content className="!tw-flex !tw-flex-col !tw-gap-4">
        <div className="tw-flex tw-flex-col">
          <Card
            containerClassName="!tw-rounded-t-[8px] !tw-rounded-b-none
          !tw-border-b tw-border-0 tw-border-solid !tw-border-gray-border"
          >
            <Card.Content className="!tw-py-4 !tw-px-5 ">
              <div className="tw-text-lg tw-font-semibold tw-text-gray-textDark">
                {t('performance:organization_overall')}
              </div>
            </Card.Content>
          </Card>
          <Card containerClassName="!tw-rounded-t-none !tw-rounded-b-[8px]">
            <Card.Content className="!tw-flex !tw-flex-col !tw-p-5 !tw-gap-5">
              {performanceLoading ? (
                <>
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-6 tw-mb-4">
                    {[1, 2, 3, 4, 5].map((index) => (
                      <div
                        key={index}
                        className="tw-flex tw-items-center tw-gap-2"
                      >
                        <Skeleton width={10} height={16} />
                        <Skeleton width={100} height={16} />
                      </div>
                    ))}
                  </div>

                  <div className="tw-flex tw-flex-col tw-gap-3">
                    <Skeleton width="100%" height={16} />
                    <div className="tw-flex tw-flex-row tw-gap-4">
                      {[1, 2, 3, 4, 5].map((index) => (
                        <div
                          key={index}
                          className="tw-flex tw-flex-col tw-gap-2"
                        >
                          <Skeleton width={60} height={32} />
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-6 tw-mb-4">
                    {legendItems?.map((item: any) => (
                      <div
                        key={item.id}
                        className="tw-flex tw-items-center tw-gap-2"
                      >
                        <div
                          className="tw-w-[10px] tw-h-4 tw-rounded-[3px]"
                          style={{ backgroundColor: item.color }}
                        />
                        <div className="tw-text-sm-plus tw-font-medium tw-text-gray-text">
                          {item.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="tw-flex tw-flex-row tw-gap-1 tw-rounded-lg tw-overflow-hidden">
                    {distributionItems?.map((item: any) => {
                      const percentage = getPercentage(item.total_evaluations);
                      const displayWidth = Math.max(percentage, 8);
                      return (
                        <div
                          key={item.id}
                          className="tw-flex tw-flex-col"
                          style={{ width: `${displayWidth}%` }}
                        >
                          <div
                            className="tw-h-4 tw-rounded-t tw-rounded-r"
                            style={{ backgroundColor: item.color }}
                          />

                          <div
                            className="tw-flex tw-pl-3 tw-pt-3 tw-border-l tw-border-solid
                           tw-border-gray-border tw-border-t-0 tw-border-b-0 tw-border-r-0"
                          >
                            <div className="tw-text-2xl tw-font-semibold tw-text-gray-900">
                              {item.total_evaluations}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </Card.Content>
          </Card>
        </div>

        <div className="tw-flex tw-flex-col">
          <Card
            containerClassName="!tw-rounded-t-[8px] !tw-rounded-b-none !tw-border-b
          tw-border-0 tw-border-solid !tw-border-gray-border"
          >
            <Card.Content className="!tw-flex !tw-flex-row !tw-justify-between tw-gap-4 !tw-py-4 !tw-px-5">
              <div className="tw-flex tw-flex-row tw-items-center">
                <span className="!tw-flex tw-text-lg tw-font-semibold tw-text-gray-textDark !tw-items-center">
                  {t('performance:percentage_distribution')}
                </span>
              </div>
              <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                <FiltersToolbar
                  searchEnabled={false}
                  predicates={predicates}
                  setPredicates={setPredicates}
                  organisation={organisation}
                  searchPlaceholder={t('common:search_list_search_placeholder')}
                />
                <Button
                  icon="download"
                  type="white"
                  size="large"
                  className="tw-mb-2"
                  onClick={() => handleExportPDF()}
                >
                  {t('performance:export')}
                </Button>
              </div>
            </Card.Content>
          </Card>
          <Card containerClassName="!tw-rounded-t-none !tw-rounded-b-[8px]">
            {distributionLoading ? (
              <Spinner centered />
            ) : displayData && displayData?.length > 0 ? (
              <Card.Content className="!tw-flex !tw-flex-col !tw-p-5 !tw-gap-5">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-6 tw-mb-4">
                  {legendItems?.map((item: any) => (
                    <div
                      key={item.id}
                      className="tw-flex tw-items-center tw-gap-2"
                    >
                      <div
                        className={`tw-w-[10px] tw-h-4 tw-rounded-[3px] ${item.color}`}
                        style={{ backgroundColor: item.color }}
                      />
                      <div className="tw-text-sm-plus tw-font-medium tw-text-gray-text">
                        {item.label}
                      </div>
                    </div>
                  ))}
                </div>
                <Overview className="tw-py-2 tw-px-5">
                  <div
                    className="tw-grid tw-grid-cols-3 tw-gap-4 tw-py-3
                  tw-border-b tw-border-gray-border tw-border-solid tw-border-0"
                  >
                    <div className="tw-font-medium tw-text-gray-muted tw-text-base tw-ml-4">
                      {group_by === 'category'
                        ? t('performance:category')
                        : t('performance:function')}
                    </div>
                    <div className="tw-font-medium tw-text-gray-muted tw-text-base">
                      {t('performance:reviews')}
                    </div>
                    <div className="tw-font-medium tw-text-gray-muted tw-text-base">
                      {t('performance:distribution')}
                    </div>
                  </div>
                  <Accordion transition transitionTimeout={250}>
                    {displayData?.map((item: any, idx: number) => {
                      const {
                        id: itemId,
                        name,
                        total_evaluations,
                        rating_scales,
                      } = item;

                      const getCategoryPercentage = (
                        value: number,
                        totalValue: number
                      ) => (totalValue > 0 ? (value / totalValue) * 100 : 0);

                      const itemCompetencies =
                        competenciesData[itemId]?.competencies || [];
                      const isLoading = loadingCompetencies[itemId];
                      const isLastItem = idx === (displayData?.length || 0) - 1;
                      const isFirstItem = idx === 0;
                      const tooltipPlacement = isLastItem
                        ? 'top'
                        : isFirstItem
                        ? 'bottom'
                        : 'bottom';

                      const handleAccordionToggle = () => {
                        handleItemClick(idx);
                      };

                      return (
                        <AccordionItem
                          category={name}
                          open={openItemIndex === idx}
                          onClick={handleAccordionToggle}
                          className={`!tw-bg-white !tw-mb-0 !tw-border-0 !tw-border-solid !tw-rounded-none
                          ${
                            isLastItem || openItemIndex === idx
                              ? '!tw-border-b-0'
                              : '!tw-border-b'
                          }`}
                          initialEntered={false}
                          customHeader={
                            <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-w-full tw-items-center">
                              <div className="tw-flex tw-flex-row tw-items-center tw-gap-1">
                                <Icon
                                  type="expand_more"
                                  className="chevron-down"
                                />
                                <div className="tw-font-medium tw-text-gray-textDarkest tw-text-base">
                                  {name}
                                </div>
                              </div>
                              <div className="tw-font-medium tw-text-gray-textDarkest tw-text-base">
                                {total_evaluations}
                              </div>
                              <PerformanceTooltip
                                placement={tooltipPlacement}
                                title={
                                  <div className="tw-py-3 tw-min-w-[180px]">
                                    <div className="tw-text-gray-400 tw-text-xs tw-font-medium tw-uppercase tw-mb-3">
                                      {t('performance:distribution')}
                                    </div>
                                    <div className="tw-flex tw-flex-col tw-gap-2">
                                      {rating_scales
                                        .filter(
                                          (scale: any) => scale.total_evaluations > 0
                                        )
                                        .map((scale: any) => (
                                          <div
                                            key={scale.id}
                                            className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-4"
                                          >
                                            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                                              <div
                                                className="tw-w-2 tw-h-2 tw-rounded-full"
                                                style={{
                                                  backgroundColor: scale.color,
                                                }}
                                              />
                                              <span className="tw-text-white tw-text-sm tw-font-medium">
                                                {scale.label}
                                              </span>
                                            </div>
                                            <span className="tw-text-white tw-text-sm tw-font-medium">
                                              {scale.total_evaluations}
                                            </span>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                }
                              >
                                <div className="tw-flex tw-flex-row tw-gap-1 tw-rounded-lg tw-overflow-hidden">
                                  {rating_scales?.map((scale: any) => {
                                    const percentage = getCategoryPercentage(
                                      scale.total_evaluations,
                                      total_evaluations
                                    );
                                    if (scale.total_evaluations === 0)
                                      return null;

                                    return (
                                      <div
                                        key={scale.id}
                                        style={{
                                          width: `${percentage}%`,
                                        }}
                                      >
                                        <div
                                          className="tw-h-4 tw-rounded-md"
                                          style={{
                                            backgroundColor: scale.color,
                                          }}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              </PerformanceTooltip>
                            </div>
                          }
                        >
                          {isLoading ? (
                            <Spinner centered />
                          ) : itemCompetencies.length > 0 ? (
                            itemCompetencies.map(
                              (competency: any, compIdx: number) => {
                                const isLastCompetency =
                                  compIdx === itemCompetencies.length - 1;
                                const isFirstCompetency = compIdx === 0;
                                const competencyTooltipPlacement =
                                  isLastCompetency
                                    ? 'top'
                                    : isFirstCompetency
                                    ? 'bottom'
                                    : 'bottom';
                                return (
                                  <div
                                    key={competency.id}
                                    className={`tw-grid tw-grid-cols-3 tw-py-4 tw-gap-4 tw-bg-gray-50
                                       tw-border-gray-border tw-border-solid tw-border-0 tw-items-center ${
                                         isLastCompetency ? '' : 'tw-border-b'
                                       }`}
                                  >
                                    <div className="tw-font-medium tw-text-gray-textDarkest tw-text-base">
                                      {competency.name}
                                    </div>
                                    <div className="tw-font-medium tw-text-gray-textDarkest tw-text-base !tw-invisible">
                                      {competency.total_evaluations}
                                    </div>
                                    <PerformanceTooltip
                                      placement={competencyTooltipPlacement}
                                      title={
                                        <div className="tw-py-3 tw-min-w-[180px]">
                                          <div className="tw-text-gray-400 tw-text-xs tw-font-medium tw-uppercase tw-mb-3">
                                            {t('performance:distribution')}
                                          </div>
                                          <div className="tw-flex tw-flex-col tw-gap-2">
                                            {competency.rating_scales
                                              .filter(
                                                (scale: any) => scale.total_evaluations > 0
                                              )
                                              .map((scale: any) => (
                                                <div
                                                  key={scale.id}
                                                  className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-4"
                                                >
                                                  <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                                                    <div
                                                      className="tw-w-2 tw-h-2 tw-rounded-full"
                                                      style={{
                                                        backgroundColor:
                                                          scale.color,
                                                      }}
                                                    />
                                                    <span className="tw-text-white tw-text-sm tw-font-normal">
                                                      {scale.label}
                                                    </span>
                                                  </div>
                                                  <span className="tw-text-white tw-text-sm tw-font-medium">
                                                    {scale.total_evaluations}
                                                  </span>
                                                </div>
                                              ))}
                                          </div>
                                        </div>
                                      }
                                    >
                                      <div className="tw-flex tw-flex-row tw-gap-1 tw-rounded-lg tw-overflow-hidden tw-ml-[6px]">
                                        {competency.rating_scales?.map(
                                          (scale: any) => {
                                            const percentage =
                                              getCategoryPercentage(
                                                scale.total_evaluations,
                                                competency.total_evaluations
                                              );
                                            if (scale.total_evaluations === 0)
                                              return null;

                                            return (
                                              <div
                                                key={scale.id}
                                                style={{
                                                  width: `${percentage}%`,
                                                }}
                                              >
                                                <div
                                                  className="tw-h-4 tw-rounded-md"
                                                  style={{
                                                    backgroundColor:
                                                      scale.color,
                                                  }}
                                                />
                                              </div>
                                            );
                                          }
                                        )}
                                      </div>
                                    </PerformanceTooltip>
                                  </div>
                                );
                              }
                            )
                          ) : (
                            <div className="tw-py-4 tw-px-4 tw-text-center tw-text-gray-muted">
                              {t(
                                'performance:no_competencies_found_for_report'
                              )}
                            </div>
                          )}
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                </Overview>
              </Card.Content>
            ) : (
              <Placeholder title={t('performance:no_data_found')} />
            )}
          </Card>
        </div>
      </Container.Content>

      <ExportModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        onExportCSV={handleExportCSV}
        onExportXLS={handleExportXLS}
        onExportPDF={handleExportPDF}
      />
    </Container>
  );
};

export default PerformanceRatingContainer;
